// Type-only: erased at build, so the client bundle never pulls in the server.
import type {
	BasketChange,
	BasketError,
	BasketItemInput,
	BasketLineView,
	BasketView,
	ProductCard,
} from "@repo/agent";

export {
	type BasketPick,
	productRowsFrom,
	productsFrom,
	type RecipeOutput,
} from "@repo/agent/product-rows";
export type { CartHandoff } from "@repo/embed";
export type { BasketChange, BasketError, BasketItemInput, ProductCard };
export type BasketLine = BasketLineView;
export type Basket = BasketView;

/** One formatter per currency: prices render on every card, every token. */
const moneyFormats = new Map<string, Intl.NumberFormat>();

export function formatMoney(amount: string, currency: string): string {
	try {
		let format = moneyFormats.get(currency);
		if (!format) {
			format = new Intl.NumberFormat("en-US", {
				style: "currency",
				currency,
			});
			moneyFormats.set(currency, format);
		}
		return format.format(Number(amount));
	} catch {
		return `${amount} ${currency}`;
	}
}

/**
 * find_ingredients as the model writes it. Every field can be missing while
 * the arguments stream in, and conversations from before recipe cards stored
 * ingredients as bare names.
 */
export interface RecipeInput {
	dish?: string;
	servings?: number;
	minutes?: number;
	ingredients?: Array<RecipeLine | string | undefined>;
	steps?: Array<string | undefined>;
}

export interface RecipeLine {
	line?: string;
	item?: string;
	pantry?: boolean;
}

/** A streamed line may still be half an object; older turns stored bare names. */
export function recipeLine(
	raw: RecipeLine | string | undefined,
): { text: string; pantry: boolean } | null {
	if (typeof raw === "string") {
		return raw.trim() ? { text: raw, pantry: false } : null;
	}
	const text = raw?.line?.trim() || raw?.item?.trim();
	return text ? { text, pantry: raw?.pantry === true } : null;
}

export function recipeDuration(minutes: number): string {
	if (minutes < 60) {
		return `${minutes} min`;
	}
	const rest = minutes % 60;
	return rest
		? `${Math.floor(minutes / 60)} h ${rest} min`
		: `${minutes / 60} h`;
}

/** The tools that change the basket; each returns the whole basket. */
export const BASKET_TOOLS = new Set([
	"add_to_basket",
	"update_basket",
	"clear_basket",
]);

/** The tool that hands the conversation to the retailer's team. */
export const ESCALATE_TOOL = "escalate_to_human";

/** The basket a tool returned, if it returned one. */
export function basketFrom(output: unknown): Basket | null {
	return (output as { basket?: Basket } | undefined)?.basket ?? null;
}
